import { BDSocketWeb } from '@mail/bd-socket';
import { getPushToken } from '../api/websocket';
import type { ApiResponse } from '../api/types';
import type { PushTokenData } from '../api/websocket';
import { refreshPipeIdGet } from '../swagger/easourcing/PushService';

/**
 * 推送事件类型
 */
export enum PushEventType {
  // 邮件消息变更推送
  COMMS_FLOW_CHANGED_MSG = 'COMMS_FLOW_CHANGED_MSG',
  // 供应商（联系人）列表未读数更新
  CONTACT_UNREAD_COUNT = 'CONTACT_UNREAD_COUNT',
  // 新增供应商（联系人）
  NEW_CONTACT = 'NEW_CONTACT',
  // request 会话中新增新消息
  SESSION_NEW_MSG = 'SESSION_NEW_MSG',
  // request 会话运行状态改变
  SESSION_TASK_RUNNING = 'SESSION_TASK_RUNNING',
  // request 会话消息变更（内容/状态等）
  SESSION_MESSAGE_CHANGED = 'SESSION_MESSAGE_CHANGED',
  // Inquiry Agent 运行日志 — 新增一条
  INQUIRY_LOG_ADDED = 'INQUIRY_LOG_ADDED',
  // Inquiry Agent 运行日志 — 更新已有条目（以 templateId 为主键）
  INQUIRY_LOG_UPDATED = 'INQUIRY_LOG_UPDATED',
  // Inquiry Agent 相关的回复被更新（新增/修改回复内容等）
  INQUIRY_REPLY_UPDATED = 'INQUIRY_REPLY_UPDATED',
  // Inquiry 联系人变更（新增/删除/状态变化）
  INQUIRY_CONTACT_CHANGE = 'INQUIRY_CONTACT_CHANGE',
  // 联系人最新消息变更（lastMsgTime 等）
  CONTACT_LAST_CHANGE = 'CONTACT_LAST_CHANGE',
}

interface WebSocketWrapMessage {
  msgGroup?: string;
  msgId: string;
  msgTime: number;
  msgType: number;
  payload: string;
  uid: string;
  message?: {
    payload?: string;
    type?: string;
  };
}

/**
 * WebSocket推送token信息
 */
export interface PushTokenInfo {
  token: string;
  shouldUseBD: boolean;
}

/**
 * WebSocket消息结构
 */
export interface WebSocketMessage<T = unknown> {
  eventType: PushEventType;
  content: T;
  timestamp: number;
}

/**
 * 消息处理回调函数类型
 */
export type MessageHandler<T = unknown> = (payload: WebSocketMessage<T>) => Record<string, unknown> | void;

/**
 * 兼容性：保持旧的MessageCallback类型，但标记为废弃
 * @deprecated 请使用 MessageHandler<T> 替代
 */
export type MessageCallback = MessageHandler<string>;

/**
 * 订阅ID类型
 */
export type SubscribeId = string;

/**
 * 应用平台类型
 */
export type AppPlatform = 'web' | 'pc' | 'mac' | 'linux' | 'android' | 'ios';

/**
 * 客户端类型
 */
export type ClientType = 'web' | 'pc' | 'mac' | 'linux';

/**
 * 连接状态
 */
export interface ConnectionState {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
}

/**
 * BDSocketWeb配置参数
 */
export interface BDSocketConfig {
  /** 产品名称 */
  product: string;
  /** 设备ID */
  deviceId: string;
  /** 应用平台 */
  appPlatform: AppPlatform;
  /** 应用版本 */
  appVersion: string;
  /** 是否开启调试模式 */
  debug?: boolean;
  /** 是否使用测试统计 */
  useTestStat?: boolean;
  /** 必达平台 API 地址 */
  apiBaseUrl?: string;
  /** 必达平台统计 API 地址 */
  statApiBaseUrl?: string;
}

/**
 * BD Socket WebSocket连接管理类
 * 职责：封装 BDSocketWeb SDK，处理连接、消息订阅、token管理
 */
export default class BDSocketManager {
  private socket: BDSocketWeb | null = null;
  private config: BDSocketConfig;
  private connectionState: ConnectionState = {
    isConnected: false,
    isConnecting: false,
    error: null,
  };
  private connectionPromise: Promise<void> | null = null;

  /** 外部回调：收到推送消息时触发 */
  onMessage: ((msg: WebSocketMessage<unknown>) => void) | null = null;
  /** 外部回调：连接状态变更时触发 */
  onStateChange: ((state: ConnectionState) => void) | null = null;

  // Token 刷新控制相关
  private isRefreshingToken: boolean = false;
  private lastTokenRefreshTime: number = 0;
  private tokenRefreshRetryCount: number = 0;
  private retryWindowStartTime: number = 0;

  // PipeId 续租相关
  private currentPipeId: string | null = null;
  private pipeIdRenewTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly PIPE_ID_RENEW_INTERVAL = 4 * 60 * 1000; // 4分钟

  // 可配置常量
  private readonly MIN_REFRESH_INTERVAL = 10 * 1000; // 最小刷新间隔：60秒
  private readonly MAX_REFRESH_RETRY_COUNT = 3; // 最大重试次数：3次
  private readonly RETRY_WINDOW_DURATION = 5 * 60 * 1000; // 重试窗口：5分钟
  private readonly REFRESH_TIMEOUT = 10 * 1000; // 刷新超时：10秒

  constructor(config: BDSocketConfig) {
    this.config = {
      ...config,
      debug: config.debug ?? false,
      useTestStat: config.useTestStat ?? false,
    };
  }

  /**
   * 获取当前连接状态
   */
  getConnectionState(): ConnectionState {
    return { ...this.connectionState };
  }

  /**
   * 更新连接状态并通知监听者
   */
  private updateConnectionState(state: Partial<ConnectionState>): void {
    this.connectionState = {
      ...this.connectionState,
      ...state,
    };
    this.onStateChange?.({ ...this.connectionState });
  }

  /**
   * 获取WebSocket连接token
   */
  private async fetchToken(): Promise<string> {
    try {
      const response: ApiResponse<PushTokenData> = await getPushToken({
        product: this.config.product,
        deviceId: this.config.deviceId,
        appPlatform: this.config.appPlatform,
        appVersion: this.config.appVersion,
      });

      if (response.data?.fp && response.data?.pipeId) {
        // 保存 pipeId
        this.currentPipeId = response.data.pipeId;
        // 启动续租
        this.startPipeIdRenewal();
        return response.data.fp;
      }
      throw new Error('Invalid token response data');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to get token';
      console.error('Failed to get WebSocket token:', message);
      throw error;
    }
  }

  /**
   * 处理 token 刷新（带频次控制）
   */
  private async handleTokenRefresh(socket: BDSocketWeb): Promise<void> {
    // 步骤 1：检查是否正在刷新
    if (this.isRefreshingToken) {
      return;
    }

    // 步骤 2：检查时间窗口限流
    const now = Date.now();
    const timeSinceLastRefresh = now - this.lastTokenRefreshTime;
    if (this.lastTokenRefreshTime > 0 && timeSinceLastRefresh < this.MIN_REFRESH_INTERVAL) {
      return;
    }

    // 步骤 3：检查重试次数限制
    // 检查是否需要重置重试窗口
    if (now - this.retryWindowStartTime > this.RETRY_WINDOW_DURATION) {
      this.tokenRefreshRetryCount = 0;
      this.retryWindowStartTime = now;
    }

    // 检查是否超过最大重试次数
    if (this.tokenRefreshRetryCount >= this.MAX_REFRESH_RETRY_COUNT) {
      this.updateConnectionState({ isConnected: false, error: 'Token refresh retry limit exceeded' });
      return;
    }

    // 步骤 4：执行刷新操作
    this.isRefreshingToken = true;
    this.tokenRefreshRetryCount++;

    // 设置超时保护
    const timeoutId = setTimeout(() => {
      this.isRefreshingToken = false;
    }, this.REFRESH_TIMEOUT);

    try {
      // 停止旧的续租定时器
      this.stopPipeIdRenewal();

      // 获取新的 token（会自动获取新的 pipeId 并启动续租）
      const newToken = await this.fetchToken();
      socket.refreshToken(newToken);
      this.lastTokenRefreshTime = Date.now();
      clearTimeout(timeoutId);
      this.isRefreshingToken = false;

      // 成功时重置重试计数
      this.tokenRefreshRetryCount = 0;
    } catch (error) {
      clearTimeout(timeoutId);
      this.isRefreshingToken = false;

      const errorMessage = error instanceof Error ? error.message : 'Token refresh failed';
      console.error(
        `Token refresh failed (attempt ${this.tokenRefreshRetryCount}/${this.MAX_REFRESH_RETRY_COUNT}):`,
        errorMessage
      );

      // 如果还有重试机会，延迟 MIN_REFRESH_INTERVAL 后自动重试
      if (this.tokenRefreshRetryCount < this.MAX_REFRESH_RETRY_COUNT) {
        setTimeout(() => {
          this.handleTokenRefresh(socket);
        }, this.MIN_REFRESH_INTERVAL);
      } else {
        // 达到最大重试次数，设置错误状态
        this.updateConnectionState({
          isConnected: false,
          error: `Token refresh failed after ${this.MAX_REFRESH_RETRY_COUNT} attempts`,
        });
      }
    }
  }

  /**
   * 注册Socket事件处理器
   */
  private registerSocketEvents(socket: BDSocketWeb): void {
    // 连接成功
    socket.on('connect', () => {
      console.log('WebSocket connection successful');
      this.updateConnectionState({ isConnected: true, isConnecting: false, error: null });
    });

    // 连接错误
    socket.on('connect_error', (error: unknown) => {
      console.error('WebSocket connection error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Connection error';
      this.updateConnectionState({ isConnected: false, isConnecting: false, error: errorMessage });
    });

    // 连接超时
    socket.on('connect_timeout', () => {
      console.error('WebSocket connection timeout');
      this.updateConnectionState({ isConnected: false, isConnecting: false, error: 'Connection timeout' });
    });

    // 断开连接
    socket.on('disconnect', (reason: unknown) => {
      console.log('WebSocket disconnected:', reason);
      this.stopPipeIdRenewal();
      this.updateConnectionState({ isConnected: false, isConnecting: false, error: null });
    });

    // 重连成功
    socket.on('reconnect', () => {
      console.log('WebSocket reconnection successful');
      this.updateConnectionState({ isConnected: true, isConnecting: false, error: null });

      if (this.currentPipeId) {
        this.immediateRenewPipeId();
      } else {
        this.handleTokenRefresh(socket);
      }
    });

    // 重连错误
    socket.on('reconnect_error', () => {
      console.error('WebSocket reconnection failed');
      this.updateConnectionState({ isConnected: false, isConnecting: false, error: 'Reconnection failed' });
    });

    // 重连彻底失败
    socket.on('reconnect_failed', () => {
      console.error('WebSocket reconnection completely failed');
      this.updateConnectionState({ isConnected: false, isConnecting: false, error: 'Reconnection completely failed' });
    });

    // 重试节点失败
    socket.on('retrynodes_failed', () => {
      console.error('WebSocket retry nodes failed');
      this.updateConnectionState({ isConnected: false, isConnecting: false, error: 'Retry nodes failed' });
    });

    // token过期，自动刷新
    socket.on('token_expired', async () => {
      await this.handleTokenRefresh(socket);
    });
  }

  /**
   * 连接WebSocket
   */
  async connect(): Promise<void> {
    // 如果已经有连接操作在进行，返回同一个 Promise
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    // 如果已经连接，直接返回
    if (this.connectionState.isConnected) {
      return;
    }

    // 创建新的连接 Promise
    this.connectionPromise = this._doConnect();

    try {
      await this.connectionPromise;
    } finally {
      this.connectionPromise = null;
    }
  }

  /**
   * 执行实际的连接操作（私有方法）
   */
  private async _doConnect(): Promise<void> {
    // 防止重复连接
    if (this.connectionState.isConnecting || this.connectionState.isConnected) {
      // console.log('WebSocket is connecting or already connected, skipping duplicate connection');
      return;
    }

    this.updateConnectionState({ isConnecting: true, error: null });

    try {
      // 清理旧连接
      this.disconnect();

      // 获取token
      const token = await this.fetchToken();

      // 确定客户端类型
      let clientType: ClientType = 'web';
      if (['pc', 'mac', 'linux'].includes(this.config.appPlatform)) {
        clientType = this.config.appPlatform as ClientType;
      }

      // 创建socket实例
      const socket = new BDSocketWeb({
        debug: this.config.debug,
        product: this.config.product,
        osVersion: this.config.appVersion,
        clientType: clientType,
        deviceId: this.config.deviceId,
        token: token,
        useTestStat: this.config.useTestStat,
        apiBaseUrl: this.config.apiBaseUrl,
        statApiBaseUrl: this.config.statApiBaseUrl,
        reconnectConfig: {
          duration: 1,
          limit: 300,
        },
      });

      // 注册事件处理
      this.registerSocketEvents(socket);

      // 订阅消息
      socket.subscribe(originalPayload => {
        try {
          console.log('🤡🤡 推送:', JSON.stringify(originalPayload, null, 2));

          // 单独解析并输出 eventType
          const payload = originalPayload as typeof originalPayload & WebSocketWrapMessage;
          const actualPayload = payload.payload;
          if (typeof actualPayload === 'string') {
            const parsedMessage: { eventType: PushEventType; content: { data: unknown }; timestamp: number } =
              JSON.parse(actualPayload);
            console.log('📌 WebSocket eventType:', parsedMessage.eventType);
          }
        } catch (error) {
          console.error('Failed to serialize WebSocket message:', error);
          console.log('WebSocket message received (raw):', originalPayload);
        }

        // 解决bida sdk ts声明写错的问题
        const payload = originalPayload as typeof originalPayload & WebSocketWrapMessage;

        try {
          // 兼容两种推送结构：
          // 1. 业务 payload 在顶层 payload
          // 2. 业务 payload 包在 eventMessage.message.payload 中
          const actualPayload = payload.payload || payload.message?.payload;

          if (typeof actualPayload === 'string') {
            // 将JSON字符串解析为对象，此时包含嵌套的 { content: { data: T } } 结构
            const parsedMessage: { eventType: PushEventType; content: { data: unknown }; timestamp: number } =
              JSON.parse(actualPayload);
            // console.log('Parsed WebSocket message:', parsedMessage);

            // 处理消息内容
            // 提取 content.data，如果不存在则使用 null
            // 保持 unknown 类型，由使用方通过泛型指定具体类型
            const webSocketMessage: WebSocketMessage<unknown> = {
              eventType: parsedMessage?.eventType,
              content: parsedMessage?.content?.data ?? null,
              timestamp: parsedMessage?.timestamp,
            };
            this.handleMessage(webSocketMessage);
          } else {
            console.warn('WebSocket message format does not match expectation:', actualPayload);
          }
        } catch (error) {
          // 如果解析失败，记录错误
          console.error('Failed to process WebSocket message:', error);
        }
        return {}; // 返回空对象满足 ICallbackMsg 类型要求
      });

      // 连接
      socket.connect();
      this.socket = socket;

      // 页面卸载时关闭连接
      if (typeof window !== 'undefined' && !window.__bdSocketUnloadEventAdded) {
        window.addEventListener('beforeunload', () => {
          this.disconnect();
        });
        window.__bdSocketUnloadEventAdded = true;
      }

      console.log('WebSocket initialization successful');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'connection failed';
      console.error('WebSocket connection failed:', errorMessage);
      this.updateConnectionState({ isConnecting: false, error: errorMessage });
      throw error;
    }
  }

  /**
   * 处理收到的消息
   */
  private handleMessage(webSocketMessage: WebSocketMessage<unknown>): void {
    if (!webSocketMessage) {
      return;
    }
    this.onMessage?.(webSocketMessage);
  }

  /**
   * 断开连接
   */
  disconnect(): void {
    // 停止 pipeId 续租
    this.stopPipeIdRenewal();

    // 清空 pipeId
    if (this.currentPipeId) {
      this.currentPipeId = null;
    }

    if (this.socket) {
      try {
        this.socket.unSubscribeAll();
        this.socket.close();
      } catch (error) {
        console.error('Failed to close WebSocket connection:', error);
      }
      this.socket = null;
    }
    this.updateConnectionState({ isConnected: false, isConnecting: false, error: null });
  }

  /**
   * 获取socket实例（调试用）
   */
  getSocket(): BDSocketWeb | null {
    return this.socket;
  }

  /**
   * 立即执行一次 pipeId 续租，然后重启定时续租
   * 用于 SDK 自动重连后立即恢复续租链
   */
  private async immediateRenewPipeId(): Promise<void> {
    if (!this.currentPipeId) return;

    try {
      await refreshPipeIdGet({ pipeId: this.currentPipeId });
      console.log('pipeId 重连后立即续租成功:', this.currentPipeId);
    } catch (error) {
      console.error('pipeId 重连后立即续租失败:', error);
    }

    this.startPipeIdRenewal();
  }

  /**
   * 启动 pipeId 续租
   */
  private startPipeIdRenewal(): void {
    // 先清理旧的定时器
    this.stopPipeIdRenewal();

    // 如果没有 pipeId，不启动续租
    if (!this.currentPipeId) {
      return;
    }

    // 使用 setTimeout 递归调用
    const scheduleNextRenewal = () => {
      this.pipeIdRenewTimer = setTimeout(async () => {
        // 检查是否还在连接状态
        if (!this.connectionState.isConnected || !this.currentPipeId) {
          console.log('WebSocket 未连接或 pipeId 已清空，停止续租');
          return;
        }

        try {
          // 调用续租接口
          await refreshPipeIdGet({ pipeId: this.currentPipeId });
          console.log('pipeId 续租成功:', this.currentPipeId);

          // 续租成功后，安排下一次续租
          scheduleNextRenewal();
        } catch (error) {
          console.error('pipeId 续租失败:', error);
          // 续租失败后继续尝试下一次续租
          scheduleNextRenewal();
        }
      }, this.PIPE_ID_RENEW_INTERVAL);
    };

    // 启动第一次续租
    scheduleNextRenewal();
  }

  /**
   * 停止 pipeId 续租
   */
  private stopPipeIdRenewal(): void {
    if (this.pipeIdRenewTimer !== null) {
      clearTimeout(this.pipeIdRenewTimer);
      this.pipeIdRenewTimer = null;
      console.log('pipeId 续租定时器已清理');
    }
  }
}

// 扩展 Window 接口
declare global {
  interface Window {
    __bdSocketUnloadEventAdded?: boolean;
  }
}
